import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DepartmentList } from '../../pages/account-config-admin/model/user.model';
import { UserService } from '../../pages/account-config-admin/service/user.service';
import { CurrentUserService } from 'src/app/pages/authentication/service/current-user.service';

export interface PostLoginDepartmentDialogData {
  organization_id: number;
  checkUserAccessLevelDepartmentAdmin: boolean;
}

@Component({
  selector: 'app-post-login-department-dialog',
  templateUrl: './post-login-department-dialog.component.html',
  styleUrls: ['./post-login-department-dialog.component.scss']
})
export class PostLoginDepartmentDialogComponent implements OnInit, OnDestroy {

  @ViewChild(MatSelect) matSelect!: MatSelect;

  departmentForm: FormGroup;
  userTeamList: DepartmentList[] = [];
  filteredTeamList: DepartmentList[] = [];
  teamFilterCtrl = new FormControl('');
  nameLength: number = 0;
  isLoading: boolean = false;
  isAddingNewDepartment: boolean = false;
  account: any;
  userEmail: string = '';

  private _destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<PostLoginDepartmentDialogComponent>,
    private fb: FormBuilder,
    private userService: UserService,
    private snackbar: MatSnackBar,
    private currentUserService: CurrentUserService,
    private liveAnnouncer: LiveAnnouncer
  ) {
    this.departmentForm = this.fb.group({
      team: new FormControl(null, Validators.required),
      name: new FormControl(null)
    });
  }

  ngOnInit(): void {
    this.loadUser();
    this.teamFilterCtrl.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._filterTeams());
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _filterTeams(): void {
    const search = (this.teamFilterCtrl.value || '').toLowerCase().trim();
    this.filteredTeamList = search
      ? this.userTeamList.filter(t => t.name.toLowerCase().includes(search))
      : this.userTeamList.slice();

    if (search && this.matSelect?.panelOpen) {
      setTimeout(() => setTimeout(() => {
        this.matSelect?.options?.forEach(opt => opt.setInactiveStyles());
      }));
    }
  }

  loadUser(): void {
    this.currentUserService.GetCurrentUser().subscribe(
      (response) => {
        this.account = response;
        this.userEmail = this.account.username || '';
        this.loadTeamList();
      });
  }

  loadTeamList(): void {
    this.isLoading = true;
    this.userService.getUserDepartmentList(this.account.new_configs.organization.id).subscribe(
      (response) => {
        this.userTeamList = response.result;
        this.filteredTeamList = this.userTeamList.slice();
        this.teamFilterCtrl.setValue('');
        this.isLoading = false;
        const count = this.userTeamList?.length ?? 0;
        this.liveAnnouncer.announce(
          count > 0 ? `${count} department${count !== 1 ? 's' : ''} loaded` : 'No departments available',
          'polite'
        );
      },
      (error) => {
        this.isLoading = false;
        this.liveAnnouncer.announce('Failed to load departments. Please try again.', 'assertive');
        console.log('Error loading departments', error);
      }
    );
  }

  toggleAddNewMode(): void {
    this.isAddingNewDepartment = !this.isAddingNewDepartment;

    const teamCtrl = this.departmentForm.get('team')!;
    const nameCtrl = this.departmentForm.get('name')!;

    if (this.isAddingNewDepartment) {
      teamCtrl.clearValidators();
      teamCtrl.reset();
      teamCtrl.updateValueAndValidity();
      nameCtrl.setValidators([Validators.required, Validators.maxLength(25)]);
      nameCtrl.updateValueAndValidity();
    } else {
      nameCtrl.clearValidators();
      nameCtrl.reset();
      this.nameLength = 0;
      nameCtrl.updateValueAndValidity();
      teamCtrl.setValidators([Validators.required]);
      teamCtrl.updateValueAndValidity();
    }
  }

  onNameChange(event: Event): void {
    this.nameLength = (event.target as HTMLInputElement).value.length;
  }

  onSelectOpenedChange(isOpen: boolean): void {
    if (!isOpen) {
      this.teamFilterCtrl.setValue('');
      setTimeout(() => {
        this.matSelect?.options?.forEach(opt => opt.setInactiveStyles());
      }, 10);
    }
  }

  onchangeTeam(event: any): void {
    console.log('Team selected', event);
  }

  onSave(): void {
    if (this.departmentForm.invalid) { return; }

    if (this.isAddingNewDepartment) {
      const name = this.departmentForm.get('name')!.value;
      this.isLoading = true;
      this.userService.createDepartmentInfo({ name, organization_id: this.account.new_configs.organization.id }).subscribe(
        (response) => {
          const newTeamId = response?.team_id;
          this.userService.addUserToTeam(newTeamId).subscribe(
            (res: any) => {
              this.isLoading = false;
              this.snackbar.open('Department created and assigned successfully!', 'OK THANKS', { duration: 5000 });
              this.liveAnnouncer.announce(`Department "${name}" created and assigned successfully.`, 'polite');
              this.dialogRef.close({ team: newTeamId });
            },
            (error) => {
              this.isLoading = false;
              const msg = error?.error?.message || 'Department created but failed to assign. Please try again.';
              this.snackbar.open(msg, 'OK THANKS', { duration: 10000 });
            }
          );
        },
        (error) => {
          this.isLoading = false;
          const msg = (error.status === 417 && error.error?.message)
            ? error.error.message
            : 'Failed to create department. Please try again.';
          this.snackbar.open(msg, 'OK THANKS', { duration: 10000 });
          this.liveAnnouncer.announce(msg, 'assertive');
        }
      );
    } else {
      const teamId = this.departmentForm.get('team')?.value;
      this.userService.addUserToTeam(teamId).subscribe(
        (res: any) => {
          this.snackbar.open(res.message || 'Department assigned successfully!', 'OK THANKS', { duration: 5000 });
          this.dialogRef.close({ team: teamId });
        },
        (error) => {
          const msg = error?.error?.message || 'Failed to assign department. Please try again.';
          this.snackbar.open(msg, 'OK THANKS', { duration: 10000 });
        }
      );
    }
  }

  get saveTooltip(): string {
    if (this.isLoading) { return 'Saving, please wait…'; }
    if (this.isAddingNewDepartment) {
      return this.departmentForm.get('name')!.invalid
        ? 'Enter a department name to continue'
        : 'Create new department and continue';
    }
    return this.departmentForm.get('team')!.invalid
      ? 'Select a department to continue'
      : 'Save selected department and continue';
  }

  onSkip(): void {
    this.dialogRef.close('skip');
  }
}